/**
 * useArtefactRehydrate.js — RC 1.55 U1 (operator-locked, 2026-05-12 PM)
 * ============================================================================
 *
 * Generic re-fetch-and-rebind hook for workspace artefacts.
 *
 * Adopts the resilient lifecycle pattern that JobDetailPage's
 * `rehydrateAnalyseFit` callback already implements
 * (`pages/JobDetailPage.js` L321–383), and exposes it as a small
 * shared hook so every workspace section can opt in without
 * duplicating the listener / lock / debounce code.
 *
 * What it does:
 *   • On every transition to `document.visibilityState === 'visible'`
 *     OR `popstate` (browser back/forward), it schedules a debounced
 *     call to `fetchAndBind()`.
 *   • A `lockRef` prevents overlapping invocations — a slow GET
 *     cannot stack a queue of pending fetches behind it.
 *   • The `enabled` gate is checked AT EFFECT-MOUNT and re-evaluated
 *     when its identity changes — sections pass a memoised boolean
 *     like `!generating && !recoveredFromInflight`.
 *   • The hook NEVER triggers `POST /api/runs` or any generation
 *     endpoint. `fetchAndBind` is the caller's READ-ONLY canonical
 *     fetch — typically `GET /api/<artefact>/{id}`.
 *
 * What it does NOT do:
 *   • No charge. No billing. No idempotency-key writes. The hook is
 *     pure read-side state sync.
 *   • No polling. If the canonical row is still pending, the caller's
 *     `fetchAndBind` is responsible for returning silently — the
 *     next visibility cycle retries.
 *   • No replacement for the dispatch-time
 *     `postTerminalHydrator` retry. They cover different races:
 *     postTerminalHydrator closes the dispatch-resolution race;
 *     this hook closes the tab-return-after-completion race.
 *
 * U1 scope (operator-locked):
 *   • Cover Letter ONLY in this slice. CV Variant + Interview Prep
 *     remain on their existing per-section URL effects until
 *     operator approves U2 / U3.
 */

import { useEffect, useRef } from 'react';

const DEFAULT_DEBOUNCE_MS = 250;

/**
 * @param {object} args
 * @param {boolean} args.enabled
 *   Top-level gate. If `false`, the hook installs no listeners and
 *   schedules nothing. Toggle off during active generation,
 *   in-flight recovery, or any state where the section's own
 *   binding path must own the next setter call.
 *
 * @param {() => Promise<void>} args.fetchAndBind
 *   The caller-provided READ-ONLY fetch-and-rebind function. Should
 *   be stable (use `useCallback`) and is expected to:
 *     – perform a canonical `apiFetch` GET
 *     – apply any alignment guards (job_id / base_cv_id / variant_id)
 *     – call the section's setters on success
 *     – silently no-op when the row is not yet readable
 *   It MUST NOT throw on expected "row still pending" — return
 *   silently. It MAY throw on hard failures; the hook will swallow
 *   them (the next visibility cycle retries).
 *
 * @param {number} [args.debounceMs=250]
 *   How long to wait after a `visibilitychange` / `popstate` event
 *   before running `fetchAndBind`. 250 ms is a sweet spot — long
 *   enough to coalesce rapid screen-lock / unlock cycles, short
 *   enough that returning to the tab feels instant.
 *
 * @param {boolean} [args.runOnMount=false]
 *   If `true`, runs `fetchAndBind` once on mount in addition to
 *   listening for visibility / popstate. Most callers leave this
 *   `false` because the existing URL effect already handles mount.
 */
export function useArtefactRehydrate({
  enabled,
  fetchAndBind,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  runOnMount = false,
}) {
  const lockRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof fetchAndBind !== 'function') return undefined;

    let cancelled = false;
    let timer = null;

    async function runOnce() {
      if (cancelled) return;
      if (lockRef.current) return;
      if (typeof document !== 'undefined'
          && document.visibilityState !== 'visible') {
        // popstate fired while the tab is hidden? Skip — the next
        // visibility cycle will retry.
        return;
      }
      lockRef.current = true;
      try {
        await fetchAndBind();
      } catch {
        // Swallow. The next visibility cycle retries. Caller may
        // log internally; the hook never surfaces toasts itself.
      } finally {
        lockRef.current = false;
      }
    }

    function schedule() {
      if (cancelled) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(runOnce, debounceMs);
    }

    function onVisible() {
      if (typeof document !== 'undefined'
          && document.visibilityState === 'visible') {
        schedule();
      }
    }

    function onPopState() {
      schedule();
    }

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('popstate', onPopState);

    if (runOnMount) schedule();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('popstate', onPopState);
    };
  }, [enabled, fetchAndBind, debounceMs, runOnMount]);
}

export default useArtefactRehydrate;
